import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isLoggedIn } from '../auth/auth';
import { formatProductPrice, getChemistryPaymentProducts, MARKET_KR } from '../payments/catalog';
import { useMarket } from '../payments/useMarket';
import { capturePaypalOrder, createPaypalOrder, loadPaypalSdk } from '../payments/paypalClient';
import { executePortOnePayment } from '../payments/portoneClient';
import { getPaymentAnalyticsContext, trackEvent, trackEventOnce, trackConfirmedPurchase } from '../utils/analytics';
import './Pricing.css';

export default function Pricing() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const paypalButtonsRef = useRef({});
  const paywallTrackedRef = useRef(false);
  const domesticCheckoutRef = useRef(false);
  const [activeProductId, setActiveProductId] = useState('');
  const [domesticProcessingId, setDomesticProcessingId] = useState('');
  const [checkoutStatus, setCheckoutStatus] = useState({ tone: '', message: '' });
  const isKorean = (i18n.resolvedLanguage || i18n.language || '').startsWith('ko');
  const { loading: marketLoading, data: marketData } = useMarket();
  const market = marketData.market;
  const isDomestic = market === MARKET_KR;
  const products = getChemistryPaymentProducts(market);
  const features = t('pricing.features', { returnObjects: true });
  const faqs = t('pricing.faqs', { returnObjects: true });

  useEffect(() => {
    if (marketLoading || paywallTrackedRef.current) return;
    paywallTrackedRef.current = true;
    trackEvent('paywall_viewed', {
      product_id: 'chemistry_pass',
      analysis_type: 'chemistry',
      market,
      currency: marketData.currency,
      source_page: 'pricing',
    });
  }, [market, marketData.currency, marketLoading]);

  useEffect(() => {
    if (!activeProductId || isDomestic) {
      return undefined;
    }

    const container = paypalButtonsRef.current[activeProductId];
    const product = products.find((candidate) => candidate.id === activeProductId);
    if (!container) {
      return undefined;
    }

    let cancelled = false;
    container.innerHTML = '';
    setCheckoutStatus({ tone: 'info', message: t('pricing.checkout.loading') });

    loadPaypalSdk()
      .then((paypal) => {
        if (cancelled || !paypal?.Buttons) {
          return;
        }

        paypal.Buttons({
          style: {
            layout: 'vertical',
            shape: 'rect',
            label: 'paypal',
          },
          createOrder: async () => {
            const order = await createPaypalOrder(activeProductId);
            trackEvent('begin_checkout', {
              currency: 'USD',
              value: product?.amount,
              payment_provider: 'paypal',
              product_id: activeProductId,
              package_type: 'chemistry_pass',
              market,
              source_page: 'pricing',
            });
            trackEventOnce('payment_start', 'paypal:' + order.order_id, {
              currency: 'USD', value: product?.amount, payment_provider: 'paypal', market,
              source_page: 'pricing', ...getPaymentAnalyticsContext(activeProductId),
            });
            return order.order_id;
          },
          onApprove: async (data) => {
            try {
              setCheckoutStatus({ tone: 'info', message: t('pricing.checkout.verifying') });
              const result = await capturePaypalOrder(data.orderID, activeProductId);
              if (result.status !== 'confirmed') throw new Error('payment_not_confirmed');
              window.dispatchEvent(new Event('premium-entitlements-updated'));
              trackConfirmedPurchase(result, {
                currency: 'USD',
                value: product?.amount,
                payment_provider: 'paypal',
                market,
                product_id: activeProductId,
                ...getPaymentAnalyticsContext(activeProductId),
                items: [{ item_id: activeProductId, item_name: activeProductId, price: product?.amount, quantity: 1 }],
              });
              setCheckoutStatus({
                tone: 'success',
                message: t('pricing.checkout.success', { count: result.credits_granted || 0 }),
              });
            } catch (error) {
              console.error('PayPal verification failed:', error);
              trackEvent('payment_failed', {
                payment_provider: 'paypal',
                product_id: activeProductId,
                currency: 'USD',
                value: product?.amount,
                failure_reason: 'verification_failed', failure_stage: 'confirmation', error_type: 'verification_failed',
                ...getPaymentAnalyticsContext(activeProductId),
              });
              setCheckoutStatus({ tone: 'error', message: t('pricing.checkout.failed') });
            }
          },
          onCancel: () => {
            trackEvent('payment_failed', {
              payment_provider: 'paypal',
              product_id: activeProductId,
              currency: 'USD',
              value: product?.amount,
              failure_reason: 'user_cancelled', failure_stage: 'cancellation', error_type: 'user_cancelled',
              ...getPaymentAnalyticsContext(activeProductId),
            });
            setCheckoutStatus({ tone: 'warning', message: t('pricing.checkout.cancelled') });
            setActiveProductId('');
          },
          onError: (error) => {
            console.error('PayPal Checkout failed:', error);
            trackEvent('payment_failed', {
              payment_provider: 'paypal',
              product_id: activeProductId,
              currency: 'USD',
              value: product?.amount,
              failure_reason: 'provider_error', failure_stage: 'provider_open', error_type: 'provider_error',
              ...getPaymentAnalyticsContext(activeProductId),
            });
            setCheckoutStatus({ tone: 'error', message: t('pricing.checkout.failed') });
          },
        }).render(container);

        setCheckoutStatus({ tone: '', message: '' });
      })
      .catch((error) => {
        console.error(error);
        trackEvent('payment_failed', {
          payment_provider: 'paypal',
          product_id: activeProductId,
          currency: 'USD',
          value: product?.amount,
          failure_reason: 'provider_error',
        });
        setCheckoutStatus({ tone: 'error', message: error.message || t('pricing.checkout.unavailable') });
      });

    return () => {
      cancelled = true;
      container.innerHTML = '';
    };
  }, [activeProductId, isDomestic, market, products, t]);

  const continueGuestAnalysis = () => {
    navigate('/chemistry-test/info', { state: { continueAsGuest: true, source: 'pricing' } });
  };

  const handlePaypalCheckout = (productId) => {
    if (!isLoggedIn()) {
      navigate(`/login?returnTo=${encodeURIComponent('/pricing')}`);
      return;
    }
    setActiveProductId(productId);
  };

  const handleDomesticCheckout = async (productId) => {
    if (domesticCheckoutRef.current) return;
    domesticCheckoutRef.current = true;
    setDomesticProcessingId(productId);
    setCheckoutStatus({ tone: 'info', message: t('pricing.checkout.card_preparing') });
    const product = products.find((candidate) => candidate.id === productId);

    try {
      const { order, confirmation } = await executePortOnePayment({
        productId,
        onOrderCreated: (createdOrder) => {
          trackEvent('begin_checkout', {
            currency: 'KRW', value: createdOrder.total_amount, payment_provider: 'portone',
            pg_provider: 'nhn_kcp', product_id: productId, package_type: 'chemistry_pass',
            market, source_page: 'pricing', ...getPaymentAnalyticsContext(productId),
          });
          trackEventOnce('payment_start', 'portone:' + createdOrder.payment_id, {
            currency: 'KRW', value: createdOrder.total_amount, payment_provider: 'portone',
            pg_provider: 'nhn_kcp', market, source_page: 'pricing',
            ...getPaymentAnalyticsContext(productId),
          });
          setCheckoutStatus({ tone: 'info', message: t('pricing.checkout.card_progress') });
        },
      });

      window.dispatchEvent(new Event('premium-entitlements-updated'));
      const verifiedProductId = confirmation.product_id || productId;
      trackConfirmedPurchase(
        { status: 'confirmed', order_id: confirmation.payment_id },
        {
          currency: 'KRW', value: order.total_amount, payment_provider: 'portone',
          pg_provider: 'nhn_kcp', market, product_id: verifiedProductId,
          ...getPaymentAnalyticsContext(verifiedProductId),
          items: [{ item_id: verifiedProductId, item_name: verifiedProductId, price: order.total_amount, quantity: 1 }],
        },
      );
      setCheckoutStatus({ tone: 'success', message: t('pricing.checkout.card_confirmed') });
      navigate('/pricing', { replace: true });
    } catch (error) {
      const failureReason = error?.failureReason || (Number(error?.status) === 0 ? 'network_error' : 'verification_failed');
      trackEvent('payment_failed', {
        currency: 'KRW', value: product?.amount, payment_provider: 'portone', pg_provider: 'nhn_kcp',
        product_id: productId, market, failure_reason: failureReason,
        failure_stage: failureReason === 'verification_failed' ? 'confirmation' : 'provider_open',
        ...getPaymentAnalyticsContext(productId),
      });
      const message = failureReason === 'user_cancelled'
        ? t('pricing.checkout.card_cancelled')
        : failureReason === 'timeout'
          ? t('pricing.checkout.card_timeout')
          : failureReason === 'configuration_error'
            ? t('pricing.checkout.card_not_configured')
            : t('pricing.checkout.card_failed');
      setCheckoutStatus({ tone: 'error', message });
    } finally {
      domesticCheckoutRef.current = false;
      setDomesticProcessingId('');
    }
  };

  const handleCheckout = (productId) => {
    if (!isLoggedIn()) {
      navigate(`/login?returnTo=${encodeURIComponent('/pricing')}`);
      return;
    }
    if (isDomestic) {
      handleDomesticCheckout(productId);
      return;
    }
    handlePaypalCheckout(productId);
  };

  if (marketLoading) {
    return <div className="pricing-page"><section className="pricing-hero"><p>{t('common.loading')}</p></section></div>;
  }

  return (
    <div className="pricing-page">
      <Helmet>
        <title>{t('pricing.meta_title')}</title>
        <meta name="description" content={t('pricing.meta_description')} />
      </Helmet>

      <section className="pricing-hero">
        <p className="pricing-eyebrow">{t('pricing.eyebrow')}</p>
        <h1>{t('pricing.title')}</h1>
        <p className="pricing-hero__copy">{t('pricing.hero_copy')}</p>
        <Link className="pricing-button pricing-button--primary pricing-hero__cta" to="/chemistry-test/info">{t('pricing.start')}</Link>
      </section>

      <section className="pricing-section" aria-labelledby="plans-title">
        <div className="pricing-section__heading">
          <p className="pricing-eyebrow">{t('pricing.simple')}</p>
          <h2 id="plans-title">{t('pricing.choose')}</h2>
          <p>{isDomestic ? t('pricing.domestic_note') : t('pricing.global_note')}</p>
        </div>
        <div className="pricing-grid">
          {products.map((product) => (
            <article className={`pricing-card ${product.badge ? 'pricing-card--featured' : ''}`} key={product.id}>
              {(isKorean ? product.badge : product.badge_en || product.badge) && <span className="pricing-card__badge">{isKorean ? product.badge : product.badge_en || product.badge}</span>}
              <h3>{isKorean ? product.title : product.title_en || product.title}</h3>
              <p className="pricing-card__price">{formatProductPrice(product, i18n.resolvedLanguage || i18n.language)}</p>
              <p className="pricing-card__description">{isKorean ? product.description : product.description_en || product.description}</p>
              <p className="pricing-card__validity">{t('pricing.validity_notice')}</p>
              {isLoggedIn() ? (
                <>
                  <button
                    className="pricing-button pricing-button--card"
                    type="button"
                    onClick={() => handleCheckout(product.id)}
                    disabled={Boolean(domesticProcessingId)}
                  >
                    {domesticProcessingId === product.id ? t('auth.processing') : t('pricing.continue_payment')}
                  </button>
                  {isDomestic && (
                    <div className="pricing-domestic-alternatives">
                      <p className="pricing-secure-note">{t('pricing.secure_card_payment')}</p>
                      <p className="pricing-other-payment-label">{t('pricing.other_payment_method')}</p>
                      <button
                        className="pricing-button pricing-button--secondary pricing-button--toss"
                        type="button"
                        onClick={() => navigate(`/payments/start/${product.id}?returnTo=${encodeURIComponent('/pricing')}&provider=toss`)}
                        disabled={Boolean(domesticProcessingId)}
                      >
                        {t('pricing.pay_with_toss')}
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="pricing-guest-actions">
                  <button
                    className="pricing-button pricing-button--card"
                    type="button"
                    onClick={() => navigate('/login?returnTo=' + encodeURIComponent('/pricing'))}
                  >
                    {isKorean ? '로그인하고 이용권 구매' : 'Sign In to Buy a Pass'}
                  </button>
                  <button
                    className="pricing-button pricing-button--secondary"
                    type="button"
                    onClick={continueGuestAnalysis}
                  >
                    {isKorean ? '비회원으로 분석하기' : 'Analyze as a Guest'}
                  </button>
                </div>
              )}
              {!isDomestic && activeProductId === product.id && (
                <div className="pricing-paypal">
                  <div ref={(node) => { paypalButtonsRef.current[product.id] = node; }} />
                </div>
              )}
            </article>
          ))}
        </div>
        {checkoutStatus.message && (
          <p className={`pricing-checkout-message pricing-checkout-message--${checkoutStatus.tone}`}>
            {checkoutStatus.message}
          </p>
        )}
      </section>

      <section className="pricing-section pricing-value" aria-labelledby="value-title">
        <div><p className="pricing-eyebrow">{t('pricing.included')}</p><h2 id="value-title">{t('pricing.what_you_get')}</h2></div>
        <ul className="pricing-feature-list">{features.map((feature) => <li key={feature}>{feature}</li>)}</ul>
      </section>

      <section className="pricing-section" aria-labelledby="faq-title">
        <div className="pricing-section__heading"><p className="pricing-eyebrow">FAQ</p><h2 id="faq-title">{t('pricing.faq_title')}</h2></div>
        <div className="pricing-faq-list">
          {faqs.map(({ question, answer }) => <details className="pricing-faq" key={question}><summary>{question}</summary><p>{answer}</p></details>)}
        </div>
      </section>

      <nav className="pricing-policy-links" aria-label={t('pricing.policies')}>
        <Link to="/terms">{t('footer.terms')}</Link><Link to="/privacy">{t('footer.privacy')}</Link><Link to="/refund-policy">{t('footer.refund')}</Link>
      </nav>
    </div>
  );
}
